import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Users, Plus, Edit2, Trash2, Search, Shield, KeyRound, Save, X, AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ROLES = [
  { value: "parent", label: "Parent", color: "bg-pink-100 text-pink-700" },
  { value: "teacher", label: "Enseignant", color: "bg-purple-100 text-purple-700" },
  { value: "admin", label: "Administration", color: "bg-orange-100 text-orange-700" },
  { value: "scolarite", label: "Resp. Scolarité", color: "bg-cyan-100 text-cyan-700" },
  { value: "finance", label: "Finance", color: "bg-green-100 text-green-700" },
  { value: "director", label: "Directeur Général", color: "bg-indigo-100 text-indigo-700" },
  { value: "founder", label: "Fondateur", color: "bg-blue-100 text-blue-700" },
  { value: "super_admin", label: "Super Admin", color: "bg-slate-800 text-white" },
  { value: "student", label: "Élève", color: "bg-yellow-100 text-yellow-700" },
];

const roleConf = (r) => ROLES.find(x => x.value === r) || { label: r, color: "bg-slate-100 text-slate-600" };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [creating, setCreating] = useState({ email: "", password: "", full_name: "", phone: "", role: "parent", otp_enabled: true });
  const [submitting, setSubmitting] = useState(false);
  const [postes, setPostes] = useState([]);
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(null);

  const resetPassword = async (u) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.full_name || u.email} ?\nUn mot de passe temporaire sera généré et le compte sera débloqué.`)) return;
    setResetting(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
      setResetResult(data);
      toast.success("Mot de passe réinitialisé ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de réinitialisation");
    } finally { setResetting(null); }
  };

  // Rôles disponibles pour création/édition (admin ne peut pas créer super_admin)
  const ASSIGNABLE_ROLES = isSuperAdmin ? ROLES : ROLES.filter(r => r.value !== "super_admin");

  const load = () => {
    setLoading(true);
    api.get("/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/rbac/postes").then(r => setPostes(r.data)).catch(() => {}); }, []);

  const filtered = users.filter(u => {
    if (filterRole !== "all" && u.role !== filterRole) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.full_name || "").toLowerCase().includes(q);
  });

  const create = async () => {
    if (!creating.email || !creating.password || !creating.full_name) {
      return toast.error("Email, mot de passe et nom complet requis");
    }
    if (creating.password.length < 6) return toast.error("Mot de passe : 6 caractères minimum");
    setSubmitting(true);
    try {
      // Si un poste personnalisé est sélectionné : rôle de base du poste + affectation RBAC
      let payload = { ...creating };
      if (creating.role.startsWith("poste:")) {
        const posteId = creating.role.slice(6);
        const poste = postes.find(p => p.id === posteId);
        payload = { ...creating, role: poste?.base_role || "staff", poste_ids: [posteId] };
      }
      await api.post("/admin/users", payload);
      toast.success(`Utilisateur ${creating.full_name} créé`);
      setCreateOpen(false);
      setCreating({ email: "", password: "", full_name: "", phone: "", role: "parent", otp_enabled: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de création");
    } finally { setSubmitting(false); }
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    const payload = {
      full_name: editTarget.full_name,
      email: editTarget.email,
      phone: editTarget.phone,
      role: editTarget.role,
      otp_enabled: editTarget.otp_enabled,
    };
    if (editTarget.new_password) {
      if (editTarget.new_password.length < 6) return toast.error("Mot de passe : 6 caractères minimum");
      payload.new_password = editTarget.new_password;
    }
    setSubmitting(true);
    try {
      await api.patch(`/admin/users/${editTarget.id}`, payload);
      // Affectation des postes (RBAC dynamique)
      if (editTarget.poste_ids_edited) {
        await api.post(`/rbac/users/${editTarget.id}/postes`, { poste_ids: editTarget.poste_ids || [] });
      }
      toast.success("Utilisateur mis à jour");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const remove = async (u) => {
    if (u.id === currentUser.id) return toast.error("Vous ne pouvez pas supprimer votre propre compte");
    if (!window.confirm(`Supprimer définitivement le compte de ${u.full_name} (${u.email}) ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const canEdit = (u) => isSuperAdmin || u.role !== "super_admin";

  return (
    <div className="space-y-5" data-testid="page-users">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="chip mb-2"><Shield className="h-3 w-3" /><span>Administration</span></div>
          <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Gestion des utilisateurs</h2>
          <p className="text-sm text-slate-500 mt-1">
            {users.length} compte(s) · {isSuperAdmin ? "Vous êtes super_admin (tous les pouvoirs)" : "Vous pouvez gérer tous les comptes sauf super_admin"}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="create-user-btn">
          <Plus className="h-4 w-4" /> Nouvel utilisateur
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou email..."
            className="pl-9"
            data-testid="users-search"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-48" data-testid="users-filter-role"><SelectValue placeholder="Tous les rôles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-400">Aucun utilisateur correspondant.</div>
        ) : (
          <table className="w-full min-w-[860px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left">Utilisateur</th>
                <th className="px-5 py-3 text-left">Email</th>
                <th className="px-5 py-3 text-left">Rôle</th>
                <th className="px-5 py-3 text-center">2FA</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(u => {
                const conf = roleConf(u.role);
                const allowed = canEdit(u);
                return (
                  <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#166534] flex items-center justify-center text-white text-xs font-bold">
                          {(u.full_name || "?").split(" ").map(p => p[0]).slice(0, 2).join("")}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-[#0F172A]">{u.full_name}</div>
                          <div className="text-xs text-slate-500">{u.phone || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-600 max-w-[220px] truncate" title={u.email}>{u.email}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex text-[11px] font-bold px-2 py-1 rounded-full ${conf.color}`}>{conf.label}</span>
                      {(u.poste_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(u.poste_ids || []).map(pid => {
                            const p = postes.find(x => x.id === pid);
                            return p ? <span key={pid} className="inline-flex text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`user-poste-badge-${u.id}-${pid}`}>🛡️ {p.name}</span> : null;
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {u.otp_enabled === false ? (
                        <span className="text-xs text-slate-400">Désactivée</span>
                      ) : (
                        <span className="text-xs text-green-700 font-bold">✓ Activée</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {!allowed ? (
                        <span className="text-[11px] text-slate-400 italic">Non modifiable</span>
                      ) : (
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditTarget({ ...u, new_password: "" })}
                            className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs gap-1"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit2 className="h-3 w-3" /> Modifier
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resetPassword(u)}
                            disabled={resetting === u.id}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs gap-1"
                            title="Réinitialiser le mot de passe et débloquer le compte"
                            data-testid={`reset-password-${u.id}`}
                          >
                            {resetting === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => remove(u)}
                            disabled={u.id === currentUser?.id}
                            className="border-red-300 text-red-700 hover:bg-red-50 text-xs gap-1 disabled:opacity-30"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Résultat de réinitialisation — mot de passe temporaire */}
      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent className="max-w-md" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-amber-600" />Mot de passe réinitialisé</DialogTitle></DialogHeader>
          {resetResult && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Compte : <strong>{resetResult.target_name}</strong> ({resetResult.target_email})
              </p>
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-center">
                <div className="text-xs uppercase font-bold text-amber-700 mb-1">Mot de passe temporaire</div>
                <div className="font-mono text-xl font-extrabold text-slate-900 select-all" data-testid="temp-password-value">{resetResult.temp_password}</div>
              </div>
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success("Copié !"); }}
                data-testid="copy-temp-password"
              >
                Copier le mot de passe
              </Button>
              <p className="text-xs text-slate-500">
                ⚠️ Communiquez-le à l&apos;utilisateur de façon sécurisée. Il devra <strong>obligatoirement le changer</strong> à sa prochaine connexion. Le compte a aussi été débloqué (tentatives échouées effacées).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResetResult(null)} className="bg-[#1E3A8A] text-white">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Créer un nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom complet *</Label>
                <Input value={creating.full_name} onChange={(e) => setCreating({ ...creating, full_name: e.target.value })} data-testid="new-user-name" />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} placeholder="+224 6XX XX XX XX" data-testid="new-user-phone" />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} data-testid="new-user-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mot de passe *</Label>
                <Input type="text" value={creating.password} onChange={(e) => setCreating({ ...creating, password: e.target.value })} placeholder="Min. 6 caractères" data-testid="new-user-password" />
              </div>
              <div>
                <Label>Rôle / Poste</Label>
                <Select value={creating.role} onValueChange={(v) => setCreating({ ...creating, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    {postes.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400">Postes personnalisés (RBAC)</div>
                        {postes.map(p => (
                          <SelectItem key={p.id} value={`poste:${p.id}`} data-testid={`new-user-poste-${p.id}`}>
                            🛡️ {p.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={creating.otp_enabled} onChange={(e) => setCreating({ ...creating, otp_enabled: e.target.checked })} className="rounded" data-testid="new-user-otp" />
              Activer la vérification en 2 étapes (recommandé)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={create} disabled={submitting} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="confirm-create-user">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Créer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Modifier l&apos;utilisateur</DialogTitle></DialogHeader>
          {editTarget && (
            <div className="space-y-3">
              <div className="text-xs bg-slate-50 p-2 rounded text-slate-600 font-mono">{editTarget.email}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nom complet</Label>
                  <Input value={editTarget.full_name || ""} onChange={(e) => setEditTarget({ ...editTarget, full_name: e.target.value })} data-testid="edit-user-name" />
                </div>
                <div>
                  <Label>Email de connexion</Label>
                  <Input type="email" value={editTarget.email || ""} onChange={(e) => setEditTarget({ ...editTarget, email: e.target.value })} placeholder="prenom.nom@gmail.com" data-testid="edit-user-email" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={editTarget.phone || ""} onChange={(e) => setEditTarget({ ...editTarget, phone: e.target.value })} data-testid="edit-user-phone" />
                </div>
              </div>
              <div>
                <Label>Rôle</Label>
                <Select value={editTarget.role} onValueChange={(v) => setEditTarget({ ...editTarget, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Postes occupés (habilitations RBAC — cumulables)</Label>
                <div className="grid grid-cols-2 gap-1.5 border border-slate-200 rounded-lg p-2 max-h-32 overflow-y-auto">
                  {postes.map(p => (
                    <label key={p.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" className="rounded"
                        checked={(editTarget.poste_ids || []).includes(p.id)}
                        onChange={() => setEditTarget(t => ({
                          ...t, poste_ids_edited: true,
                          poste_ids: (t.poste_ids || []).includes(p.id)
                            ? (t.poste_ids || []).filter(x => x !== p.id)
                            : [...(t.poste_ids || []), p.id],
                        }))}
                        data-testid={`edit-user-poste-${p.id}`} />
                      {p.name}
                    </label>
                  ))}
                  {postes.length === 0 && <span className="text-xs text-slate-400 col-span-2">Aucun poste — créez-en dans « Postes & habilitations »</span>}
                </div>
              </div>
              <div>
                <Label>Réinitialiser le mot de passe (optionnel)</Label>
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-slate-400" />
                  <Input type="text" value={editTarget.new_password || ""} onChange={(e) => setEditTarget({ ...editTarget, new_password: e.target.value })} placeholder="Laisser vide pour ne pas changer" data-testid="edit-user-new-password" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editTarget.otp_enabled !== false} onChange={(e) => setEditTarget({ ...editTarget, otp_enabled: e.target.checked })} className="rounded" data-testid="edit-user-otp" />
                Vérification en 2 étapes activée
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Annuler</Button>
            <Button onClick={saveEdit} disabled={submitting} className="bg-[#1E3A8A] hover:bg-[#172554] text-white" data-testid="confirm-edit-user">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Enregistrer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
